/* eslint-disable no-nested-ternary */
import { NavigationContainerRefWithCurrent } from '@react-navigation/native'
import { IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshControl } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Box } from 'widgets/Box'
import { ChatInputAction } from 'widgets/ChatInputAction'
import { FlatList } from 'widgets/FlatList'
import { LoadingSpinner } from 'widgets/LoadingSpinner'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDMRoom } from 'hooks/useDMRoom'
import { useLiveMessageQuery } from 'hooks/useLiveMessageQuery'
import { useMessages } from 'hooks/useMessages'
import { useQueryClient } from 'hooks/useQueryClient'

import { Message, RoomTypes } from 'types/chat'
import { UserType } from 'types/user'

import { MessageCard } from './widgets/MessageCard'

type ChatRoomProps = {
  roomType: RoomTypes
  roomId: string
  onMessageLongPress: (reportedAccount: UserType) => void
  onMessageSend?: () => void
  isBottomSheetPresentation?: boolean
  drawerNavigationRef?: NavigationContainerRefWithCurrent<ReactNavigation.RootParamList>
}

export const ChatRoom = ({
  roomType,
  roomId,
  onMessageLongPress,
  onMessageSend,
  isBottomSheetPresentation = false,
  drawerNavigationRef,
}: ChatRoomProps) => {
  const { data: user } = useCurrentUser()
  const theme = useTheme()
  const queryClient = useQueryClient()

  const [messageBody, setMessageBody] = useState('')

  const {
    updateLastViewed: { mutate: updateLastViewedMutate },
  } = useDMRoom({ roomId })

  const {
    createMessage,
    data: messages,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useMessages({ roomId })
  useLiveMessageQuery({ roomId })

  const updateLastViewed = useCallback(() => {
    user?.id && updateLastViewedMutate({ userId: user.id })
  }, [user, updateLastViewedMutate])

  const sendMessage = useCallback(async () => {
    await createMessage.mutateAsync({ messageBody })
    setMessageBody('')
    updateLastViewed()
    onMessageSend && onMessageSend()
  }, [createMessage, messageBody, onMessageSend, updateLastViewed])

  // Update last viewed on mount and unmount
  useEffect(() => {
    updateLastViewed()

    return updateLastViewed
  }, [updateLastViewed])

  const insets = useSafeAreaInsets()
  const isGroup = useMemo(() => roomType === RoomTypes.PROGRAM, [roomType])
  const safeAreaStyle = isGroup ? {} : { paddingBottom: insets.bottom }

  const memoizedMessages = useMemo(() => {
    const uniqueIds: string[] = []
    return messages.filter((msg) => {
      const isDuplicate = uniqueIds.includes(msg.id)
      if (!isDuplicate) {
        uniqueIds.push(msg.id)
      }
      return !isDuplicate
    })
  }, [messages])

  return (
    <Box display='flex' flex={1} padding={{ bottom: 0 }}>
      <Box backgroundColor='white' padding={{ top: 'md' }} flex={1}>
        <FlatList<Message>
          data={memoizedMessages}
          refreshControl={
            !IS_WEB ? (
              <RefreshControl
                refreshing={isLoading || isFetching || isFetchingNextPage}
                onRefresh={() => queryClient.invalidateQueries(['rooms', roomId])}
                progressBackgroundColor={theme.colors.v2primary}
                tintColor={theme.colors.v2primary}
              />
            ) : undefined
          }
          inverted
          keyExtractor={(message) => `${message.id}`}
          loading={isLoading}
          noContentProps={{
            message:
              roomType === RoomTypes.PROGRAM
                ? 'There is no conversation here yet, why not start one!'
                : roomType === RoomTypes.DIRECT
                ? 'This is the start of your conversation with this user.'
                : roomType === RoomTypes.POST
                ? 'This post has no comments yet, but you can start the conversation!'
                : roomType === RoomTypes.COMPLETION
                ? 'This activity was completed, but has no comments yet. Let them know how much they crushed it!'
                : '',
          }}
          onEndReached={fetchNextPage}
          renderItem={({ item: message }) => (
            <MessageCard
              isCurrentUser={message.creator.id === user?.id}
              message={message}
              onMessageLongPress={onMessageLongPress}
              isComment={isBottomSheetPresentation}
              drawerNavigationRef={drawerNavigationRef}
            />
          )}
          ListHeaderComponent={isLoading ? <LoadingSpinner /> : undefined}
        />
      </Box>
      <Box
        backgroundColor='white'
        flexDirection='row'
        display='flex'
        width='100%'
        style={safeAreaStyle}
      >
        <Box
          flex={1}
          margin={{
            top: 'smd',
            left: 'smd',
            right: 'smd',
            bottom: IS_WEB || isGroup ? 'smd' : undefined,
          }}
        >
          <ChatInputAction
            action={sendMessage}
            disabled={!messageBody}
            value={messageBody}
            isBottomSheetPresentation={isBottomSheetPresentation}
            inputProps={{
              disabled: createMessage.isLoading,
              onChange:
                IS_WEB || !isBottomSheetPresentation
                  ? () => setMessageBody
                  : (text: string) => setMessageBody(text),
              onSubmitEditing: sendMessage,
              placeholder: 'Write a message',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
